import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { StatusPill } from "@/lib/orderStatus";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  ArrowLeft,
  Paintbrush,
  Truck,
  User,
  Mail,
  Phone,
  MapPin,
  CreditCard,
  Pencil,
  Store,
  FileText,
  ClipboardList,
  Building2,
  ExternalLink,
  StickyNote,
  BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";

const PAINT_OPTIONS = [
  { value: "awaiting", label: "Awaiting paint" },
  { value: "at_painter", label: "At painter" },
  { value: "returned", label: "Returned" },
  { value: "ready", label: "Ready" },
];

const PaintStatus = ({ status }) => {
  if (!status || status === "not_required")
    return <span className="text-xs text-muted-foreground">No paint</span>;
  const map = {
    awaiting: "bg-[#E5A93D]/15 text-[#7c5a14] border-[#E5A93D]/40",
    at_painter: "bg-[#D66853]/15 text-[#a44231] border-[#D66853]/40",
    returned: "bg-primary/10 text-primary border-primary/30",
    ready: "bg-[#2C4C3B]/15 text-[#2C4C3B] border-[#2C4C3B]/40",
  };
  return <span className={`pill capitalize ${map[status]}`}>{status.replace("_", " ")}</span>;
};

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [paintDialog, setPaintDialog] = useState(null); // item
  const [paintStatus, setPaintStatus] = useState("awaiting");
  const [paintNote, setPaintNote] = useState("");
  const [delDialog, setDelDialog] = useState(false);
  const [delDate, setDelDate] = useState("");
  const [delDriver, setDelDriver] = useState("");
  const [editItem, setEditItem] = useState(null); // edit cost/paint flag
  const [editNeedsPaint, setEditNeedsPaint] = useState(false);
  const [editColor, setEditColor] = useState("");
  const [editCost, setEditCost] = useState("");
  const [stripeData, setStripeData] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
    if (data?.stripe_payment_intent) {
      try {
        const { data: pay } = await api.get(`/orders/${id}/payment`);
        setStripeData(pay);
      } catch {
        setStripeData(null);
      }
    }
  };

  useEffect(() => { load(); }, [id]);

  if (!order) return <div className="p-8 text-muted-foreground">Loading…</div>;

  const lineSubtotal = order.items.reduce(
    (s, it) => s + (it.line_total ?? it.unit_price * it.qty),
    0,
  );
  const subtotal = order.subtotal || lineSubtotal;
  const totalCost = order.items.reduce((s, it) => s + (it.unit_cost || 0) * it.qty, 0);
  const total = order.total ?? subtotal;
  const profit = total - totalCost;
  const margin = total ? (profit / total) * 100 : 0;

  const submitPaint = async () => {
    try {
      await api.patch(`/orders/${id}/items/${paintDialog.id}/paint`, {
        status: paintStatus,
        note: paintNote || null,
      });
      toast.success("Paint status updated");
      setPaintDialog(null);
      setPaintNote("");
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  const scheduleDelivery = async () => {
    if (!delDate) { toast.error("Pick a date"); return; }
    try {
      await api.post("/deliveries", {
        order_id: id,
        scheduled_for: delDate,
        driver: delDriver || null,
        address: order.customer.address,
      });
      toast.success("Delivery scheduled");
      setDelDialog(false);
    } catch {
      toast.error("Failed to schedule");
    }
  };

  const openEditItem = (it) => {
    setEditItem(it);
    setEditNeedsPaint(!!it.needs_paint);
    setEditColor(it.paint_color || "");
    setEditCost(it.unit_cost ? String(it.unit_cost) : "");
  };

  const saveItemEdit = async () => {
    try {
      await api.patch(`/orders/${id}/items/${editItem.id}/flag`, {
        needs_paint: editNeedsPaint,
        paint_color: editNeedsPaint ? (editColor || null) : null,
        unit_cost: editCost === "" ? null : parseFloat(editCost),
      });
      toast.success("Item updated");
      setEditItem(null);
      await load();
    } catch {
      toast.error("Update failed");
    }
  };

  return (
    <div className="p-6 lg:p-10 max-w-[1400px] animate-fade-in">
      <Link
        to="/orders"
        data-testid="back-to-orders"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Orders
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <div className="label-eyebrow">Order detail</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2 font-mono">
            {order.number}
          </h1>
          <div className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-2">
            <StatusPill status={order.status} />
            <span>·</span>
            <span>Placed {fmtDate(order.created_at)}</span>
            {order.store_name && (
              <>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><Store className="w-3.5 h-3.5" />{order.store_name}</span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Dialog open={delDialog} onOpenChange={setDelDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="schedule-delivery-btn">
                <Truck className="w-4 h-4 mr-2" /> Schedule delivery
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Schedule delivery</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Date</Label>
                  <Input
                    data-testid="delivery-date"
                    type="date"
                    value={delDate}
                    onChange={(e) => setDelDate(e.target.value)}
                  />
                </div>
                <div>
                  <Label>Driver (optional)</Label>
                  <Input
                    data-testid="delivery-driver"
                    placeholder="e.g. Marcus"
                    value={delDriver}
                    onChange={(e) => setDelDriver(e.target.value)}
                  />
                </div>
                <Button
                  data-testid="delivery-submit"
                  onClick={scheduleDelivery}
                  className="bg-primary hover:bg-[#203A2C] w-full"
                >
                  Schedule
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: items + payments */}
        <div className="lg:col-span-2 space-y-6">
          <div className="surface-card p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-eyebrow">Line items</div>
                <h2 className="font-heading text-xl font-semibold mt-1">{order.items.length} items</h2>
              </div>
            </div>
            <div className="overflow-x-auto -mx-2">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wider text-muted-foreground">
                  <tr className="border-b border-border/70">
                    <th className="text-left font-medium px-2 py-2">Item</th>
                    <th className="text-left font-medium px-2 py-2">Paint</th>
                    <th className="text-right font-medium px-2 py-2">Qty</th>
                    <th className="text-right font-medium px-2 py-2">Unit</th>
                    <th className="text-right font-medium px-2 py-2 hidden md:table-cell">Cost</th>
                    <th className="text-right font-medium px-2 py-2">Line</th>
                    <th className="text-right font-medium px-2 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {order.items.map((it) => {
                    const lineTotal = it.line_total ?? it.unit_price * it.qty;
                    return (
                      <tr key={it.id} className="border-b border-border/40">
                        <td className="px-2 py-3">
                          <div className="font-medium">{it.name}</div>
                          <div className="text-xs text-muted-foreground font-mono">{it.sku}{it.modification ? ` · ${it.modification}` : ""}</div>
                          {it.paint_color && (
                            <div className="text-xs text-[#D66853] mt-0.5">Color: {it.paint_color}</div>
                          )}
                          {(it.build_fee > 0 || it.modification_fee > 0) && (
                            <div className="text-[11px] text-muted-foreground mt-0.5">
                              {it.build_fee > 0 && <>Build {fmtCurrency(it.build_fee)} </>}
                              {it.modification_fee > 0 && <>Mod {fmtCurrency(it.modification_fee)}</>}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-3"><PaintStatus status={it.paint_status} /></td>
                        <td className="px-2 py-3 text-right num">{it.qty}</td>
                        <td className="px-2 py-3 text-right num">{fmtCurrency(it.unit_price)}</td>
                        <td className="px-2 py-3 text-right num hidden md:table-cell text-muted-foreground">
                          {it.unit_cost ? fmtCurrency(it.unit_cost) : <span className="italic">—</span>}
                        </td>
                        <td className="px-2 py-3 text-right num font-semibold">
                          {fmtCurrency(lineTotal)}
                        </td>
                        <td className="px-2 py-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              data-testid={`edit-item-${it.sku}`}
                              onClick={() => openEditItem(it)}
                              title="Edit cost / paint"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            {it.needs_paint && (
                              <Button
                                size="sm"
                                variant="ghost"
                                data-testid={`paint-update-${it.sku}`}
                                onClick={() => {
                                  setPaintDialog(it);
                                  setPaintStatus(it.paint_status === "not_required" ? "awaiting" : it.paint_status);
                                }}
                              >
                                <Paintbrush className="w-4 h-4 text-[#D66853]" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5} className="px-2 py-3 text-right text-muted-foreground">Subtotal</td>
                    <td className="px-2 py-3 text-right num font-semibold">{fmtCurrency(subtotal)}</td>
                    <td></td>
                  </tr>
                  {order.discount_amount > 0 && (
                    <tr>
                      <td colSpan={5} className="px-2 py-1 text-right text-muted-foreground">
                        Discount{order.discount_percentage ? ` (${order.discount_percentage}%)` : ""}
                      </td>
                      <td className="px-2 py-1 text-right num text-[#a44231]">-{fmtCurrency(order.discount_amount)}</td>
                      <td></td>
                    </tr>
                  )}
                  {order.shipping_cost > 0 && (
                    <tr>
                      <td colSpan={5} className="px-2 py-1 text-right text-muted-foreground">Shipping</td>
                      <td className="px-2 py-1 text-right num">{fmtCurrency(order.shipping_cost)}</td>
                      <td></td>
                    </tr>
                  )}
                  {order.tax > 0 && (
                    <tr>
                      <td colSpan={5} className="px-2 py-1 text-right text-muted-foreground">Sales tax</td>
                      <td className="px-2 py-1 text-right num">{fmtCurrency(order.tax)}</td>
                      <td></td>
                    </tr>
                  )}
                  <tr className="border-t border-border/70">
                    <td colSpan={5} className="px-2 py-3 text-right font-semibold">Total</td>
                    <td className="px-2 py-3 text-right num text-lg font-bold">{fmtCurrency(total)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Paint timeline */}
          <div className="surface-card p-6">
            <div className="label-eyebrow mb-1">Paint timeline</div>
            <h2 className="font-heading text-xl font-semibold mb-4">Item movement history</h2>
            {order.items.flatMap((it) =>
              (it.paint_events || []).map((ev, i) => ({ ev, it, key: `${it.id}-${i}` })),
            ).sort((a, b) => (b.ev.at || "").localeCompare(a.ev.at || "")).length === 0 ? (
              <div className="text-sm text-muted-foreground">No paint events yet.</div>
            ) : (
              <div className="space-y-4">
                {order.items
                  .flatMap((it) =>
                    (it.paint_events || []).map((ev, i) => ({ ev, it, key: `${it.id}-${i}` })),
                  )
                  .sort((a, b) => (b.ev.at || "").localeCompare(a.ev.at || ""))
                  .map(({ ev, it, key }) => (
                    <div key={key} className="flex gap-3">
                      <div className="w-2 h-2 rounded-full bg-[#D66853] mt-2 shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm">
                          <span className="font-medium">{it.name}</span>{" "}
                          <span className="text-muted-foreground">→ {ev.status.replace("_", " ")}</span>
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {fmtDate(ev.at)} {ev.by ? `· ${ev.by}` : ""} {ev.note ? `· ${ev.note}` : ""}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>

        {/* Right: customer + finance */}
        <div className="space-y-6">
          {order.notes && (
            <div className="surface-card p-5 border-l-4 border-l-[#E5A93D]" data-testid="order-notes">
              <div className="label-eyebrow flex items-center gap-2 mb-2">
                <StickyNote className="w-3.5 h-3.5" /> Order notes
              </div>
              <p className="text-sm whitespace-pre-wrap">{order.notes}</p>
            </div>
          )}

          <div className="surface-card p-6">
            <div className="label-eyebrow mb-3 flex items-center gap-2">
              <FileText className="w-3.5 h-3.5" /> Order info
            </div>
            <div className="space-y-2 text-sm">
              {order.po_number && <Row label="PO #" value={<span className="font-mono">{order.po_number}</span>} />}
              {order.store_name && <Row label="Store" value={order.store_name} />}
              {order.unit_name && <Row label="Unit" value={order.unit_name} />}
              {order.sales_rep && <Row label="Sales rep" value={order.sales_rep} />}
              {order.assigned_to && <Row label="Assigned to" value={order.assigned_to} />}
              {order.payment_code && <Row label="Payment code" value={<span className="font-mono">{order.payment_code}</span>} />}
              {order.revision_number > 0 && <Row label="Revision" value={`#${order.revision_number}`} />}
              {order.created_by && <Row label="Created by" value={order.created_by} muted />}
            </div>
          </div>

          <div className="surface-card p-6">
            <div className="label-eyebrow mb-3 flex items-center gap-2">
              <User className="w-3.5 h-3.5" /> Customer
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4 text-muted-foreground" />
                <span className="font-medium">{order.customer.name}</span>
              </div>
              {order.customer.company && (
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-muted-foreground" />
                  <span>{order.customer.company}</span>
                </div>
              )}
              {order.customer.email && (
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-muted-foreground" />
                  <a className="hover:underline truncate" href={`mailto:${order.customer.email}`}>{order.customer.email}</a>
                </div>
              )}
              {order.customer.additional_email && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground pl-6">
                  cc: <a className="hover:underline" href={`mailto:${order.customer.additional_email}`}>{order.customer.additional_email}</a>
                </div>
              )}
              {order.customer.phone && (
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-muted-foreground" />
                  <span>{order.customer.phone}</span>
                </div>
              )}
              {order.customer.address && (
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 text-muted-foreground mt-0.5" />
                  <span>{order.customer.address}</span>
                </div>
              )}
            </div>
          </div>

          <div className="surface-card p-6">
            <div className="label-eyebrow mb-3 flex items-center gap-2">
              <Truck className="w-3.5 h-3.5" /> Shipping &amp; delivery
            </div>
            <div className="space-y-2 text-sm">
              {order.delivery_method && (
                <Row label="Method" value={<span className="capitalize">{order.delivery_method}</span>} />
              )}
              {order.carrier && <Row label="Carrier" value={order.carrier} />}
              {order.tracking_number && (
                <Row label="Tracking" value={<span className="font-mono text-xs break-all">{order.tracking_number}</span>} />
              )}
              {order.driver_name && <Row label="Driver" value={order.driver_name} />}
              {order.driver_email && (
                <Row label="Driver email" value={<a className="hover:underline" href={`mailto:${order.driver_email}`}>{order.driver_email}</a>} muted />
              )}
              {order.route_id && <Row label="Route" value={<span className="font-mono text-xs">{order.route_id}</span>} muted />}
              {order.scheduled_pickup_date && (
                <Row label="Pickup scheduled" value={fmtDate(order.scheduled_pickup_date)} />
              )}
              {order.estimated_delivery && (
                <Row label="Est. delivery" value={fmtDate(order.estimated_delivery)} />
              )}
              {order.shipped_date && (
                <Row label="Shipped" value={fmtDate(order.shipped_date)} />
              )}
              {order.delivered_at && (
                <Row label="Delivered" value={fmtDate(order.delivered_at)} accent />
              )}
              {order.shipping_label_url && (
                <a href={order.shipping_label_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline inline-flex items-center gap-1 pt-2">
                  Shipping label <ExternalLink className="w-3.5 h-3.5" />
                </a>
              )}
              {!order.delivery_method && !order.carrier && !order.tracking_number && !order.scheduled_pickup_date && (
                <div className="text-xs text-muted-foreground italic">No shipping info yet.</div>
              )}
            </div>
          </div>

          <div className="surface-card p-6">
            <div className="label-eyebrow mb-3 flex items-center gap-2">
              <BadgeCheck className="w-3.5 h-3.5" /> Workflow
            </div>
            <Timeline events={[
              { label: "Created", at: order.created_at },
              { label: "Inventory checked", at: order.inventory_checked_at, by: order.inventory_checked_by },
              { label: "Fulfillment started", at: order.fulfillment_started_at },
              { label: "Picked", at: order.picked_at },
              { label: "Packed", at: order.packed_at },
              { label: "Ready for shipment", at: order.ready_for_shipment_at },
              { label: "Shipped", at: order.shipped_date },
              { label: "Delivered", at: order.delivered_at },
            ].filter((e) => e.at)} />
          </div>

          <div className="surface-card p-6">
            <div className="label-eyebrow mb-3">Profit breakdown</div>
            <div className="space-y-2 text-sm">
              <Row label="Revenue" value={fmtCurrency(total)} />
              <Row label="Cost (COGS)" value={fmtCurrency(totalCost)} muted />
              {order.tariff_amount > 0 && <Row label="Tariff" value={fmtCurrency(order.tariff_amount)} muted />}
              {order.credit_applied > 0 && <Row label="Credit applied" value={fmtCurrency(order.credit_applied)} muted />}
              <div className="border-t border-border/60 my-2" />
              <Row label="Profit" value={fmtCurrency(profit)} bold accent />
              <Row label="Margin" value={`${margin.toFixed(1)}%`} muted />
            </div>
          </div>

          <div className="surface-card p-6">
            <div className="label-eyebrow mb-3 flex items-center gap-2"><CreditCard className="w-3.5 h-3.5" />Payment</div>
            <div className="space-y-2 text-sm">
              <Row label="Method" value={(order.payment_method || "—").toUpperCase()} />
              {order.payment_status && (
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <span className="pill capitalize bg-primary/10 text-primary border-primary/30">
                    {order.payment_status.replace(/_/g, " ")}
                  </span>
                </div>
              )}
              <Row label="Total" value={fmtCurrency(total)} />
              <Row label="Paid" value={fmtCurrency(order.paid || 0)} />
              <Row label="Balance" value={fmtCurrency(order.balance || 0)} accent={!(order.balance > 0)} />
              {order.is_deposit_order && (
                <>
                  <div className="border-t border-border/60 my-2" />
                  <div className="label-eyebrow text-[10px]">Deposit order</div>
                  {order.deposit_amount > 0 && (
                    <Row label="Deposit" value={`${fmtCurrency(order.deposit_amount)} (${order.deposit_percentage}%)`} />
                  )}
                  {order.balance_paid_date && <Row label="Balance paid" value={fmtDate(order.balance_paid_date)} accent />}
                </>
              )}
              {order.ach_funds_available_date && (
                <Row label="ACH available" value={fmtDate(order.ach_funds_available_date)} muted />
              )}
              {order.receipt_email_sent && (
                <div className="text-xs text-primary inline-flex items-center gap-1">
                  <BadgeCheck className="w-3.5 h-3.5" /> Receipt emailed
                </div>
              )}
              {order.stripe_payment_intent && (
                <div className="pt-2 text-[11px] font-mono text-muted-foreground break-all">
                  {order.stripe_payment_intent}
                </div>
              )}
            </div>

            {stripeData && stripeData.ok && (
              <div className="mt-4 pt-4 border-t border-border/60 space-y-2 text-sm">
                <div className="label-eyebrow text-[10px]">Live from Stripe</div>
                {stripeData.status && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span className="pill bg-primary/10 text-primary border-primary/30 capitalize">{stripeData.status.replace(/_/g, " ")}</span>
                  </div>
                )}
                {stripeData.amount != null && (
                  <Row label="Amount" value={fmtCurrency((stripeData.amount || 0) / 100)} />
                )}
                {stripeData.fee != null && (
                  <Row label="Stripe fee" value={fmtCurrency((stripeData.fee || 0) / 100)} muted />
                )}
                {stripeData.net != null && (
                  <Row label="Net to bank" value={fmtCurrency((stripeData.net || 0) / 100)} accent />
                )}
                {stripeData.amount_refunded > 0 && (
                  <Row label="Refunded" value={fmtCurrency((stripeData.amount_refunded || 0) / 100)} />
                )}
                {stripeData.card_brand && stripeData.card_last4 && (
                  <Row label="Card" value={`${stripeData.card_brand.toUpperCase()} •••• ${stripeData.card_last4}`} />
                )}
                {stripeData.ach_bank_name && (
                  <Row label="Bank" value={`${stripeData.ach_bank_name} •••• ${stripeData.ach_last4 || ""}`} />
                )}
                {stripeData.number && (
                  <Row label="Invoice #" value={stripeData.number} muted />
                )}
                <div className="flex flex-wrap gap-3 pt-2">
                  {stripeData.receipt_url && (
                    <a href={stripeData.receipt_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline">Receipt →</a>
                  )}
                  {stripeData.hosted_invoice_url && (
                    <a href={stripeData.hosted_invoice_url} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline">Hosted invoice →</a>
                  )}
                  {stripeData.invoice_pdf && (
                    <a href={stripeData.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-primary text-sm hover:underline">PDF →</a>
                  )}
                </div>
              </div>
            )}
            {stripeData && !stripeData.ok && (
              <div className="mt-4 pt-4 border-t border-border/60 text-xs text-muted-foreground">
                {stripeData.reason === "stripe_permission" ? (
                  <span>Live Stripe data needs <span className="font-semibold text-foreground">Credit Notes Read</span> permission on the API key. Add it in the Stripe Dashboard.</span>
                ) : stripeData.reason === "no_stripe_id" ? (
                  "No Stripe ID attached to this order."
                ) : (
                  <>Live Stripe data unavailable: {stripeData.detail || stripeData.reason}</>
                )}
              </div>
            )}
          </div>

          {(order.measurement_files?.length > 0 || order.schematic_files?.length > 0) && (
            <div className="surface-card p-6">
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                <FileText className="w-3.5 h-3.5" /> Files
              </div>
              <div className="space-y-3">
                {order.measurement_files?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Measurements</div>
                    {order.measurement_files.map((f, i) => (
                      <FileLink key={i} file={f} />
                    ))}
                  </div>
                )}
                {order.schematic_files?.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1.5">Schematics</div>
                    {order.schematic_files.map((f, i) => (
                      <FileLink key={i} file={f} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {order.specialty_details && Object.keys(order.specialty_details).length > 0 && (
            <div className="surface-card p-6">
              <div className="label-eyebrow mb-3">Specialty details</div>
              <pre className="text-xs font-mono bg-secondary/40 p-3 rounded-md overflow-x-auto max-h-64">
                {JSON.stringify(order.specialty_details, null, 2)}
              </pre>
              {order.specialty_status_tracking && (
                <>
                  <div className="label-eyebrow mt-4 mb-2">Status tracking</div>
                  <pre className="text-xs font-mono bg-secondary/40 p-3 rounded-md overflow-x-auto max-h-64">
                    {JSON.stringify(order.specialty_status_tracking, null, 2)}
                  </pre>
                </>
              )}
            </div>
          )}

          {order.modification_log && (
            <div className="surface-card p-6">
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                <ClipboardList className="w-3.5 h-3.5" /> Modification log
              </div>
              <p className="text-sm whitespace-pre-wrap text-muted-foreground">{order.modification_log}</p>
            </div>
          )}
        </div>
      </div>

      {/* Paint dialog */}
      <Dialog open={!!paintDialog} onOpenChange={(v) => !v && setPaintDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Update paint status</DialogTitle></DialogHeader>
          {paintDialog && (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="font-medium">{paintDialog.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{paintDialog.sku}</div>
                {paintDialog.paint_color && (
                  <div className="text-xs text-[#D66853] mt-1">{paintDialog.paint_color}</div>
                )}
              </div>
              <div>
                <Label>New status</Label>
                <Select value={paintStatus} onValueChange={setPaintStatus}>
                  <SelectTrigger data-testid="paint-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAINT_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value} data-testid={`paint-status-${o.value}`}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Note (optional)</Label>
                <Textarea
                  data-testid="paint-note"
                  value={paintNote}
                  onChange={(e) => setPaintNote(e.target.value)}
                  placeholder="e.g. Dropped at Allied Paint Shop"
                />
              </div>
              <Button
                data-testid="paint-submit"
                onClick={submitPaint}
                className="w-full bg-primary hover:bg-[#203A2C]"
              >
                Save update
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit item dialog: cost + paint flag */}
      <Dialog open={!!editItem} onOpenChange={(v) => !v && setEditItem(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit item</DialogTitle></DialogHeader>
          {editItem && (
            <div className="space-y-4">
              <div className="text-sm">
                <div className="font-medium">{editItem.name}</div>
                <div className="text-xs text-muted-foreground font-mono">{editItem.sku}</div>
              </div>
              <div>
                <Label>Unit cost (your cost from supplier)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editCost}
                  onChange={(e) => setEditCost(e.target.value)}
                  placeholder="0.00"
                  data-testid="edit-item-cost"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Base44 doesn&apos;t expose cost; set it here so profit/margin reflect reality.
                </div>
              </div>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={editNeedsPaint}
                  onCheckedChange={(v) => setEditNeedsPaint(!!v)}
                  data-testid="edit-item-needs-paint"
                />
                <span className="text-sm">This item needs paint</span>
              </label>
              {editNeedsPaint && (
                <div>
                  <Label>Paint color</Label>
                  <Input
                    value={editColor}
                    onChange={(e) => setEditColor(e.target.value)}
                    placeholder="e.g. Sage Green"
                    data-testid="edit-item-color"
                  />
                </div>
              )}
              <Button
                data-testid="edit-item-submit"
                onClick={saveItemEdit}
                className="w-full bg-primary hover:bg-[#203A2C]"
              >
                Save
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Row({ label, value, bold, accent, muted }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span
        className={`num text-right break-words ${bold ? "font-bold text-lg" : ""} ${accent ? "text-primary" : ""} ${muted ? "text-muted-foreground" : ""}`}
      >
        {value}
      </span>
    </div>
  );
}

function Timeline({ events }) {
  if (!events?.length) {
    return <div className="text-xs text-muted-foreground italic">No workflow events yet.</div>;
  }
  return (
    <div className="space-y-3">
      {events.map((e, i) => (
        <div key={i} className="flex gap-3 text-sm">
          <div className="w-1.5 h-1.5 rounded-full bg-primary mt-2 shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="font-medium">{e.label}</div>
            <div className="text-xs text-muted-foreground">
              {fmtDate(e.at)} {e.by ? `· ${e.by}` : ""}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function FileLink({ file }) {
  if (!file) return null;
  if (typeof file === "string") {
    return (
      <a href={file} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1 break-all">
        <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {file.split("/").pop()}
      </a>
    );
  }
  const url = file.url || file.href || file.file_url;
  const name = file.name || file.filename || (url ? url.split("/").pop() : "File");
  if (!url) return <div className="text-sm text-muted-foreground">{name}</div>;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline inline-flex items-center gap-1 break-all">
      <ExternalLink className="w-3.5 h-3.5 shrink-0" /> {name}
    </a>
  );
}
